import { isMatchWithRegex } from '../../../helpers/isMatchWithRegex';
import { TAttributeDictionary } from '../types';
import TranslationHelper from '../../../helpers/TranslationHelper';
import { object, ValidationError, AnySchema } from 'yup';

const getRegexErrors = (
    attributes: TAttributeDictionary
): Record<string, string> => {
    const errors: Record<string, string> = {};

    for (const key in attributes) {
        const { value, regex, dictionary } = attributes[key];

        if (
            value &&
            regex &&
            (dictionary === undefined || dictionary.length === 0)
        ) {
            if (!isMatchWithRegex(regex, value)) {
                errors[`${key}.value`] =
                    TranslationHelper.translate('Invalid format');
            }
        }
    }

    return errors;
};

export const validateRegexErrors = (attributes: TAttributeDictionary) => {
    const regexErrors = getRegexErrors(attributes);
    if (Object.keys(regexErrors).length > 0) {
        return { attributes: regexErrors };
    }
    return null;
};

export const mergeErrors = (
    schemaErrors: Record<string, any> | null,
    regexErrors: Record<string, any> | null
) => {
    if (!schemaErrors && !regexErrors) return null;

    return {
        ...(schemaErrors || {}),
        attributes: {
            ...(schemaErrors?.attributes || {}),
            ...(regexErrors?.attributes || {}),
        },
    };
};

export const getCombinedSchema = (
    schema: any,
    attributes: TAttributeDictionary
) => {
    const attributesSchemaObject = Object.values(attributes).reduce(
        (acc, field) => {
            acc[field.name] = object({
                value: field.validation || ('' as any),
            });
            return acc;
        },
        {} as any
    );

    const attributesSchema = object().shape(attributesSchemaObject);
    return object().shape({
        data: schema,
        attributes: attributesSchema,
    });
};

export const validateSchema = async <T>(
    schema: AnySchema,
    values: T
): Promise<null | ReturnType<typeof getErrors>> => {
    try {
        await schema.validate(values, { abortEarly: false });
        return null;
    } catch (err: any) {
        return getErrors(err);
    }
};
export const getErrors = (err: any) => {
    return err.inner.reduce(
        (acc: { [key: string]: any }, error: ValidationError) => {
            const path = error.path || '';
            const message = error.errors?.[0] || '';
            const translatedMessage = TranslationHelper.translate(message);

            //assign errors to attributes or data part of form
            const [prefix, ...rest] = path.split('.');
            const key = rest.join('.');

            acc[prefix] = acc[prefix] || {};
            acc[prefix][key] = translatedMessage;

            return acc;
        },
        {}
    );
};

export const validateAllErrors = async <T>(
    schema: AnySchema,
    attributes: TAttributeDictionary,
    values: T
): Promise<Record<string, any> | null> => {
    const regexErrors = validateRegexErrors(attributes);
    const combinedSchema = getCombinedSchema(schema, attributes);
    const schemaErrors = await validateSchema(combinedSchema, values);

    return mergeErrors(schemaErrors, regexErrors);
};
